/**
 * Product Media Gallery navigation module.
 *
 * Handles [data-produt-media-gallery] prev/next buttons inside the zoom dialog (mobile):
 * - Navigates between drag-zoom-wrapper items in the dialog gallery
 * - Scrolls the media-gallery__grid in background to stay in sync
 *
 * Also listens to 'zoom-media:selected' events so that manually scrolling
 * the dialog also syncs the grid.
 */

class ProductMediaGalleryNav {
  /** @type {number} */
  #currentIndex = 0;

  #controller = new AbortController();

  /** @type {Element} */
  #mediaGallery;

  /** @param {Element} mediaGallery */
  constructor(mediaGallery) {
    this.#mediaGallery = mediaGallery;
    this.#init();
  }

  #init() {
    const { signal } = this.#controller;

    this.#mediaGallery.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {Element} */ (event.target).closest('[data-produt-media-gallery]');
        if (!btn) return;

        const direction = /** @type {HTMLElement} */ (btn).dataset.produtMediaGallery === 'prev' ? -1 : 1;
        this.#navigate(direction);
      },
      { signal }
    );

    // Sync current index whenever zoom-dialog reports a scroll-based selection
    this.#mediaGallery.addEventListener(
      'zoom-media:selected',
      (event) => {
        const index = /** @type {CustomEvent & { detail: { index: number } }} */ (event).detail?.index;
        if (typeof index === 'number') {
          this.#currentIndex = index;
          this.#scrollGrid(index);
        }
      },
      { signal }
    );

    // Swipe navigation on mobile zoomed gallery
    const gallery = this.#mediaGallery.querySelector('.dialog-zoomed-gallery');
    if (gallery) {
      /** @type {number} */
      let touchStartX = 0;
      /** @type {number} */
      let touchStartY = 0;

      gallery.addEventListener(
        'touchstart',
        (event) => {
          const touch = /** @type {TouchEvent} */ (event).touches[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        },
        { signal, passive: true }
      );

      gallery.addEventListener(
        'touchend',
        (event) => {
          const touch = /** @type {TouchEvent} */ (event).changedTouches[0];
          if (!touch) return;

          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;

          // Require a clear horizontal swipe
          if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

          // Don't navigate if the current slide is zoomed in
          const media = this.#dialogMedia;
          const currentMedia = media[this.#currentIndex];
          const wrapper = currentMedia?.querySelector('drag-zoom-wrapper');
          if (wrapper) {
            const scale = parseFloat(getComputedStyle(wrapper).getPropertyValue('--drag-zoom-scale')) || 1;
            if (scale > 1) return;
          }

          this.#navigate(deltaX < 0 ? 1 : -1);
        },
        { signal, passive: true }
      );
    }
  }

  /** @returns {HTMLElement[]} */
  get #dialogMedia() {
    return Array.from(this.#mediaGallery.querySelectorAll('.dialog-zoomed-gallery > li'));
  }

  /** @returns {import('./slideshow').Slideshow | null} */
  get #slideshow() {
    return /** @type {any} */ (this.#mediaGallery).slideshow ?? null;
  }

  /**
   * Navigate to the previous or next item in the zoom dialog.
   * @param {-1 | 1} direction
   */
  #navigate(direction) {
    const media = this.#dialogMedia;
    if (!media.length) return;

    this.#currentIndex = (this.#currentIndex + direction + media.length) % media.length;

    media[this.#currentIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.#slideshow?.select(this.#currentIndex, undefined, { animate: false });
    this.#scrollGrid(this.#currentIndex);
  }

  /**
   * Scroll the media-gallery__grid to the item at the given index.
   * @param {number} index
   */
  #scrollGrid(index) {
    const grid = this.#mediaGallery.querySelector('.media-gallery__grid');
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll(':scope > li'));
    items[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  destroy() {
    this.#controller.abort();
  }
}

for (const gallery of document.querySelectorAll('media-gallery')) {
  if (gallery.querySelector('[data-produt-media-gallery]')) {
    new ProductMediaGalleryNav(gallery);
  }
}